import { camelCase, merge, set } from 'lodash-es'
import { validate as uuidValidate } from 'uuid'

import { TableColumn } from '@/types/Table'

export type BuildQueryVariablesOptions = {
  pageSize?: number
  sortKey?: string
  sortDirection?: 'asc' | 'desc'
  searchableColumns?: string[]
  fullTextSearchColumns?: string[]
  filters?: any[]
  matchAllColumns?: boolean
  search?: string
  columns: any[]
}

const buildDotNotationFilter = (columnName: string, thingToSet: any) => {
  const camelCasedPathName = columnName
    .split('.')
    .map((part: string) => camelCase(part))
    .join('.')

  const processedFilter = set({}, camelCasedPathName, thingToSet)

  return processedFilter
}

const buildQueryVariables = (options: BuildQueryVariablesOptions) => {
  const isMobile = window.innerWidth <= 991

  const defaults = {
    first: isMobile ? 10 : 20,
  }

  if (!options) {
    return defaults
  }

  const {
    pageSize = defaults.first,
    sortKey,
    sortDirection,
    searchableColumns = [],
    fullTextSearchColumns = [],
    filters = [],
    matchAllColumns,
    search = '',
  } = options

  const variables: any = {
    ...defaults,
    first: pageSize,
  }

  if (sortKey && sortDirection) {
    if (Array.isArray(sortKey)) {
      variables.orderBy = sortKey.map(key =>
        `${key}_${sortDirection}`.toUpperCase(),
      )
    } else {
      variables.orderBy = `${sortKey}_${sortDirection}`.toUpperCase()
    }

    if (sortKey.includes('formField')) {
      variables.orderBy = undefined
      variables.argFormFieldId = sortKey.split('__')[1]
      variables.argOrderDirection = sortDirection.toUpperCase()
    }
  }

  const buildFormFieldFilter = (
    formFieldId: string,
    value: any,
    filter: TableColumn,
  ) => {
    if (value === null) {
      return {
        formFieldId: { equalTo: formFieldId },
        or: [
          {
            value: { isNull: true },
          },
          {
            value: { equalTo: '' },
          },
        ],
      }
    }

    if (value?.type === 'interval') {
      const type: string =
        options.columns.find(({ key }) => key === `formField__${formFieldId}`)
          ?.type ?? 'number'

      const valueKey =
        {
          number: 'numericValue',
          date: 'value',
          rating: 'numericValue',
        }[type] ?? 'value'

      return {
        formFieldId: { equalTo: formFieldId },
        [valueKey]: {
          greaterThanOrEqualTo: value.min,
          lessThanOrEqualTo: value.max,
        },
      }
    }

    return {
      formFieldId: { equalTo: formFieldId },
      value: filter?.isEnum
        ? { equalTo: value.toString() }
        : { includesInsensitive: value.toString() },
    }
  }

  const buildArrayFilter = (
    filter: any,
    positiveFilters: any[],
    negativeFilters: any[],
  ) => {
    let overlap = {}
    let noOverlap = {}
    if (filter.matchAll) {
      overlap = positiveFilters.length
        ? {
            ...buildDotNotationFilter(filter.field, {
              contains: positiveFilters,
            }),
          }
        : {}

      noOverlap = negativeFilters.length
        ? {
            not: {
              ...buildDotNotationFilter(filter.field, {
                overlaps: negativeFilters,
              }),
            },
          }
        : {}
    } else {
      overlap = positiveFilters.length
        ? {
            ...buildDotNotationFilter(filter.field, {
              overlaps: positiveFilters,
            }),
          }
        : {}

      noOverlap = negativeFilters.length
        ? {
            not: {
              ...buildDotNotationFilter(filter.field, {
                contains: negativeFilters,
              }),
            },
          }
        : {}
    }

    if (positiveFilters.length && negativeFilters.length) {
      if (filter.matchAll) {
        return {
          and: [{ ...overlap }, { ...noOverlap }],
        }
      } else {
        return {
          or: [{ ...overlap }, { ...noOverlap }],
        }
      }
    }

    return {
      ...overlap,
      ...noOverlap,
    }
  }

  const buildIntervalFilter = (
    filter: any,
    positiveFilters: any[],
    negativeFilters: any[],
  ) => {
    const intervalFilter = {
      or: positiveFilters
        .filter(value => value !== null)
        .map(positiveFilter => ({
          ...buildDotNotationFilter(filter.field, {
            greaterThanOrEqualTo: positiveFilter.min,
            lessThanOrEqualTo: positiveFilter.max,
          }),
        })),
      and: negativeFilters
        .filter(value => value !== null)
        .map(negativeFilter => ({
          not: {
            ...buildDotNotationFilter(filter.field, {
              greaterThanOrEqualTo: negativeFilter.min,
              lessThanOrEqualTo: negativeFilter.max,
            }),
          },
        })),
    }

    if (positiveFilters.includes(null)) {
      intervalFilter.or = [
        ...intervalFilter.or,
        {
          ...buildDotNotationFilter(filter.field, {
            isNull: true,
          }),
        },
      ]
    }

    if (negativeFilters.includes(null)) {
      intervalFilter.and = [
        ...intervalFilter.and,
        {
          not: {
            ...buildDotNotationFilter(filter.field, {
              isNull: true,
            }),
          },
        },
      ]
    }

    return intervalFilter
  }

  const buildColumnFilter = (filterWithQueries: any[]) =>
    filterWithQueries.map(filter => {
      const negativeFilters = filter.negativeQueries
      const positiveFilters = filter.queries.filter(
        (query: any) => !negativeFilters.includes(query),
      )

      if (filter.field.includes('formField')) {
        const formFieldId = filter.field.split('formField__')[1]

        const condition = filter.matchAll ? 'and' : 'or'

        const overlap = positiveFilters.length
          ? {
              some: {
                active: {
                  equalTo: true,
                },
                [condition]: positiveFilters.map((value: any) =>
                  buildFormFieldFilter(formFieldId, value, filter),
                ),
              },
            }
          : {}

        const noOverlap = negativeFilters.length
          ? {
              none: {
                active: {
                  equalTo: true,
                },
                [condition]: negativeFilters.map((value: any) =>
                  buildFormFieldFilter(formFieldId, value, filter),
                ),
              },
            }
          : {}

        const noExistence = positiveFilters.filter(
          (filter: any) => filter === null,
        ).length
          ? {
              none: {
                formFieldId: { equalTo: formFieldId },
              },
            }
          : {}

        const existence = negativeFilters.filter(
          (filter: any) => filter === null,
        ).length
          ? {
              some: {
                formFieldId: { equalTo: formFieldId },
              },
            }
          : {}

        if (
          Object.keys(noExistence).length === 0 &&
          Object.keys(existence).length === 0
        ) {
          return {
            formFieldEntries: { ...overlap, ...noOverlap },
          }
        }

        const result =
          negativeFilters.filter((filter: any) => filter === null).length > 0
            ? {
                and: [
                  { formFieldEntries: { ...overlap, ...noOverlap } },
                  { formFieldEntries: { ...existence, ...noExistence } },
                ],
              }
            : {
                or: [
                  { formFieldEntries: { ...overlap, ...noOverlap } },
                  { formFieldEntries: { ...existence, ...noExistence } },
                ],
              }

        return result
      }

      if (filter.isArray) {
        return buildArrayFilter(filter, positiveFilters, negativeFilters)
      }

      if (filter.isInterval) {
        return buildIntervalFilter(filter, positiveFilters, negativeFilters)
      }

      const isUuid = (value: any) => uuidValidate(value)

      return {
        or: [
          {
            or: positiveFilters.map((value: any) =>
              buildDotNotationFilter(
                filter.field,
                value === null
                  ? { isNull: true }
                  : isUuid(value) || typeof value === 'boolean' || filter.isEnum
                  ? { equalTo: value }
                  : { includesInsensitive: value },
              ),
            ),
          },
          {
            and: negativeFilters.map((value: any) =>
              buildDotNotationFilter(
                filter.field,
                value === null
                  ? { isNull: false }
                  : isUuid(value) || typeof value === 'boolean' || filter.isEnum
                  ? { notEqualTo: value }
                  : { notIncludesInsensitive: value },
              ),
            ),
          },
        ],
      }
    })

  // Process column filters
  const filterWithQueries = filters.filter(filter => filter.queries.length > 0)
  if (filterWithQueries.length) {
    const columnFilters = buildColumnFilter(filterWithQueries)

    variables.filter = {
      ...variables.filter,
      ...(matchAllColumns ? { and: columnFilters } : { or: columnFilters }),
    }
  }

  if (search.length) {
    const searchFilter = buildSearch(
      searchableColumns,
      fullTextSearchColumns,
      search,
    )

    variables.filter = merge(variables.filter, searchFilter)

    variables.argSearchString = search
  }

  return variables
}

export const buildSearch = (
  searchableColumns: string[] = [],
  fullTextSearchColumns: string[] = [],
  search = '',
) => {
  const buildSearchPart = (column = '', isFullText = false) => {
    const parts = column.split('.')
    const obj = {}

    parts.reduce((acc: any, part: string, i: number) => {
      acc[part] = acc[part] || {}

      if (i === parts.length - 1) {
        if (isFullText) {
          acc[part].matches = search
        } else {
          acc[part].includesInsensitive = search
        }
      }

      return acc[part]
    }, obj)

    return obj
  }

  return {
    or: [
      ...searchableColumns.map(column => buildSearchPart(column)),
      ...fullTextSearchColumns.map(column => buildSearchPart(column, true)),
    ],
  }
}

export default buildQueryVariables
